import { useParams } from "react-router-dom";
import { Bell, Pencil, CalendarClock, RotateCw } from "lucide-react";
import { PageHeader } from "@/components/control/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  Section, Loadable, Status, Info, History, ActionButton, RunButton, BackLink, useLoad, fmt, can, TZ_OPTIONS,
} from "@/components/control/kit";
import { jobFields } from "@/pages/Notifications";

function Actions({ d, reload }) {
  const { user } = useAuth();
  const j = d.job;
  const groups = useLoad(j.status === "entwurf" && can(user, "notifications:manage") ? "/notifications/groups" : null);
  const reachable = d.recipients?.reachable ?? 0;
  return (
    <>
      <Status value={j.status} testId="job-status" />
      {j.status === "entwurf" && groups.data && (
        <ActionButton label="Bearbeiten" icon={Pencil} testId="edit-notification" fields={jobFields(groups.data.groups, j)} onDone={reload} submitLabel="Speichern"
          page="bearbeiten" title="Benachrichtigung verfassen" pageSubtitle="Wähle erst Inhalt und Kanal. Die Empfängerprüfung folgt danach."
          onSubmit={async (v) => { await api.patch(`/notifications/jobs/${j.id}`, v); return "Entwurf gespeichert."; }} />
      )}
      {j.status === "entwurf" && can(user, "notifications:send") && d.recipients && reachable === 0 && (
        <p className="max-w-md rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" role="note" data-testid="notification-no-recipients">Keine erreichbaren Empfänger. Ein Versand ist erst möglich, wenn mindestens ein Mitglied erreichbar ist.</p>
      )}
      {j.status === "entwurf" && can(user, "notifications:send") && reachable > 0 && (
        <ActionButton label="Versand prüfen und planen" icon={CalendarClock} variant="default" testId="schedule-notification" onDone={reload}
          page="versand" title="Versand prüfen und planen" pageSubtitle="Der angezeigte Versand ist noch nicht ausgeführt."
          submitLabel="Versand bestätigen" disabled={!d.recipients} pageSubject={j.title}
          pageRows={[["Titel", j.title], ["Empfänger", reachable === 1 ? "1 erreichbares Kreismitglied" : `${reachable} erreichbare Kreismitglieder`], ["Kanal", "In-App in der Mitglieder-App"]]}
          fields={[
            { type: "info", text: `Erreichbar: ${reachable} Empfänger. Ohne Datum wird sofort versendet.` },
            { name: "scheduled_at", label: "Versandzeitpunkt (optional)", type: "datetime-local" },
            { name: "timezone", label: "Zeitzone", type: "select", options: TZ_OPTIONS, default: "Europe/Zurich", placeholder: false },
            { name: "confirm", label: `Ich bestätige den Versand an ${reachable} Empfänger.`, type: "checkbox", required: true },
          ]}
          onSubmit={async (v) => {
            const { data } = await api.post(`/notifications/jobs/${j.id}/schedule`, { scheduled_at: v.scheduled_at, timezone: v.timezone, confirm_count: reachable });
            return data.status === "geplant" ? "Versand geplant." : `Versand abgeschlossen: ${data.status}.`;
          }} />
      )}
      {["teilweise fehlgeschlagen", "fehlgeschlagen"].includes(j.status) && can(user, "notifications:send") && (
        <RunButton label="Fehlgeschlagene erneut senden" icon={RotateCw} testId="retry-notification" success="Erneuter Versand ausgeführt." onDone={reload}
          run={() => api.post(`/notifications/jobs/${j.id}/retry`)} />
      )}
    </>
  );
}

function JobView({ d, reload }) {
  const j = d.job;
  const r = d.recipients;
  return (
    <>
      <PageHeader title={j.title} subtitle={`Kanal: Mitglieder-App · erstellt ${fmt(j.created_at)}`} icon={Bell} actions={<Actions d={d} reload={reload} />} />
      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="Nachricht" testId="job-message">
          <Info items={[["Text", j.body], ["Empfängerkreis", j.target?.group_name], ["E-Mail", j.email], ["Versandzeitpunkt", fmt(j.scheduled_at)], ["Versendet", fmt(j.sent_at)]]} />
        </Section>
        <Section title="Empfänger" testId="job-recipients">
          {!r ? <p className="text-sm text-muted-foreground">Bitte zuerst einen Empfängerkreis festlegen.</p> : (
            <Info items={[["Mitglieder im Kreis", r.group_members], ["Erreichbar", r.reachable],
              ...Object.entries(r.excluded).map(([k, v]) => [`Ausgeschlossen: ${k}`, v])]} />
          )}
        </Section>
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Section title="Versandergebnis und Fehler" testId="job-result">
          {!j.result ? <p className="text-sm text-muted-foreground">Noch nicht versendet.</p> : (
            <Info items={[["Geplant", j.result.total], ["Zugestellt", j.result.delivered], ["Fehlgeschlagen", j.result.failed],
              ...Object.entries(j.result.failures || {}).map(([k, v]) => [`Grund: ${k}`, v])]} />
          )}
        </Section>
        <Section title="Verlauf" testId="job-history"><History items={j.history} /></Section>
      </div>
    </>
  );
}

export default function NotificationDetail() {
  const { id } = useParams();
  const st = useLoad(`/notifications/jobs/${id}`);
  return (
    <div>
      <BackLink to="/benachrichtigungen">Zurück zu Benachrichtigungen</BackLink>
      <Loadable state={st}>{(d) => <JobView d={d} reload={st.reload} />}</Loadable>
    </div>
  );
}
